import { observable, action, reaction } from 'mobx'
import _ from 'lodash'
import { streamTypeEnum } from '@common/enums'
import { isIE, swapArray } from '@utils'

import { LiveRoom, Common } from '@services/api'
import { SDK_PLATFORM, addliveDataSoureQN } from '@views/WebLive/LiveRoom/qiniu-sdk'


export interface IAnchors {
  anchorId: string
  /** 主播名称 */
  anchorName: string
  /** 简介 */
  remark: string
  /** 主播头像 */
  anchorPhoto: string
  /** 上麦主播 */
  isCurrentLiveAnchor: boolean
  userId: string
}
export interface ILiveSource {
  sid?: string
  url: string
  switchType?: string
  videoType: string
  sourceType: string
  videoUserImg?: string
  videoUserName?: string
  hasVideo?: boolean
  hasAudio?: boolean
  uid?: string
  poster?: string
  isHideSmallWindow?: boolean
}
interface IMicData {
  connectType: string
  userId: string
  videoType: string
}
export type TLiveDataSource = ILiveSource[]

const INIT_LIVEROOMDETAIL = {
  roomName: '',
  globalMute: '1',
  isMute: '1',
  anchors: [] as IAnchors[],
  liveMode: 0,
  isSchoolLive: 0,
  windowLayout: '',
  anchorOrgName: '',
  anchorName: '',
  roomId: '',
  platform: 3, // 平台 3 为采取了七牛底层 其他采取 腾讯底层 默认后续采取7牛
  viewReportInterval: 60, // 默认直播上报频率60s
  replayReportInterval: 60, // 默认回放上报频率60s
}

type ILiveRoomDetail = typeof INIT_LIVEROOMDETAIL

const INIT_REPORTSTREAMDATA = {
  timestamp: 0,
  diffServerTime: 0,
  streamId: '', // 直播时 sid、回放时 fileId
}

type IReportStreamData = typeof INIT_REPORTSTREAMDATA

const INIT_REPORTSTREAMALLDATA = {
  needRestartReport: false, // 是否需要重启上传
  playing: false, // 是否播放视频
  showAgainLoadModal: false, // 显示异常Modal
  needReportType: 0, // 0 不需要 1 需要 2发起上报 3上报完成需要重置数据
  resetDataTypes: [] as string[], // 重置数据类型
  main: INIT_REPORTSTREAMDATA as IReportStreamData,
  share: INIT_REPORTSTREAMDATA as IReportStreamData,
  line1: INIT_REPORTSTREAMDATA as IReportStreamData,
  line2: INIT_REPORTSTREAMDATA as IReportStreamData,
}

type IReportStreamAllData = typeof INIT_REPORTSTREAMALLDATA

class LiveStreamState {
  @observable public masterId = ''
  @observable public isStepOut = false
  @observable public noticeList = []
  @observable public liveDataSource = [] as TLiveDataSource
  @observable public micData = [] as IMicData[]
  @observable public liveRoomDetail: ILiveRoomDetail = INIT_LIVEROOMDETAIL
  @observable public playUrlPreObj = {} // 播放地址前缀
  @observable public reportStreamData: IReportStreamAllData = INIT_REPORTSTREAMALLDATA  // 流数据上报
  @observable private lastLiveDataSource = [] as TLiveDataSource
  @observable public globalSensitiveWord = []
  @observable public arrUrl: string[] = []
  @observable public voiceValue = 100 // 直播音量

  @action
  public getNoticeList = async (roomId: string) => {
    const resp: any = await LiveRoom.getNoticeByRoomId(roomId)
    this.noticeList = _.get(resp, 'body.infos', [])
  }

  @action
  public setVoiceValue = (value: number) => {
    this.voiceValue = value
  }

  @action
  public setArrUrl = (url: string) => {
    this.arrUrl.push(url)
  }
  @action
  public setArrReset = () => {
    this.arrUrl = []
  }
  @action
  public clearRoomInfo = () => {
    this.liveRoomDetail = INIT_LIVEROOMDETAIL
  }

  @action
  public addliveDataSoure = async (source: any[] = []) => {
    // 走七牛逻辑
    if (this.liveRoomDetail.platform === SDK_PLATFORM.QI_NIU) {
      addliveDataSoureQN.call(this, source)
      return
    }
    for (const stream of source) {
      if (
        this.liveDataSource.length === 0 ||
        this.liveDataSource.filter((data: any) => data.sid === stream.sid)
          .length === 0
      ) {
        let videoType = 'line'
        // 防止多主播场景主播轮麦
        const anchor = this.liveRoomDetail.anchors.filter(
          (data: any) => data.userId === stream.uid
        )[0]
        // 防止多主播场景主播轮麦
        if (anchor && this.masterId !== anchor.userId) {
          this.masterId = anchor.userId
        }
        if (
          stream.uid === this.masterId ||
          (!stream.uid && this.liveRoomDetail.liveMode === 4)
        ) {
          if (
            source.filter(item => item.uid === this.masterId).length === 1 &&
            this.liveDataSource.length === 0
          ) {
            videoType = 'main'
          } else {
            videoType = [
              streamTypeEnum.PC_CAERMA,
              streamTypeEnum.OUT_CAERMA_WINDOW,
            ].includes(+stream.st)
              ? 'main'
              : (
                videoType = [
                  streamTypeEnum.PC_SHARE_DES,
                  streamTypeEnum.PC_SHARE_WINDOW,
                  streamTypeEnum.PC_MEDIA,
                ].includes(+stream.st) ? 'share' : 'main'
              )
          }
        }
        let userImg = ''
        let userName = ''
        if (videoType === 'line') {
          const res = await LiveRoom.getAnchorDetail(
            this.liveRoomDetail.roomId,
            stream.uid
          )
          userImg = _.get(res, 'body.userImg', '')
          userName =
            _.get(res, 'body.userName', '') || _.get(res, 'body.nickName', '')
        }
        if (videoType === 'main') {
          userName = '主播'
          // 只跟新一条，且是主播流，说明主播重新上麦了，解决主播异常退出的场景
          if (source.length === 1) {
            this.liveDataSource = []
          }
        }
        if (videoType === 'share') {
          this.liveDataSource.unshift(
            this.formdataVideoSource(stream, videoType, userImg, userName)
          )
        } else {
          this.liveDataSource.push(
            this.formdataVideoSource(stream, videoType, userImg, userName)
          )
        }
      }
    }
    if (this.liveDataSource.length > 0) {
      this.isStepOut = false
    } else {
      this.isStepOut = true
    }
  }
  @action
  public clearReportStreamData = () => {
    this.reportStreamData = INIT_REPORTSTREAMALLDATA
  }
  @action
  public setNeedRestartReport = () => {
    this.reportStreamData.needRestartReport = true
  }
  @action
  public startReportStreamData = async () => {
    this.reportStreamData.playing = true
    // 主流时间戳没有则需要请求系统时间戳接口和当前时间戳进行校验修正
    let mainSid = ''
    this.liveDataSource.forEach(item => {
      if (item.videoType === 'main') {
        mainSid = `${item.sid}`
      }
    })
    await this.handleStreamStamp(mainSid, 'main')
    if (this.liveDataSource.length > 1) {
      this.liveDataSource.filter(item => item.videoType === 'share').forEach(item => {
        this.handleStreamStamp(`${item.sid}`, item.videoType, true)
      })
      const lineLength = this.liveDataSource.filter(item => item.videoType === 'line').length
      if (lineLength > 0) {
        const line1Source = this.liveDataSource.filter(item => item.videoType === 'line')[0]
        this.handleStreamStamp(`${line1Source.sid}`, 'line1', true)
        if (lineLength === 2) {
          const line2Source = this.liveDataSource.filter(item => item.videoType === 'line')[1]
          this.handleStreamStamp(`${line2Source.sid}`, 'line2', true)
        }
      }
    }
  }
  @action
  public updateReportStreamData = (key: string, value: any) => {
    this.reportStreamData[key] = value
  }
  private handleStreamStamp = async (streamId ,videoType, start = false) => {
    if (videoType === 'main') {
      if (!this.reportStreamData.main.timestamp) {
        const res: any = await Common.getGlobalTimestamp().catch(() => {
          this.reportStreamData.showAgainLoadModal = true
          return
        })
        if (!res || !res.body) {
          this.reportStreamData.showAgainLoadModal = true
          return
        }
        this.reportStreamData.main.timestamp = parseInt(res.body.timestamp, 10)
        this.reportStreamData.main.diffServerTime = this.reportStreamData.main.timestamp - Date.now()
        this.reportStreamData.main.streamId = streamId
      }
    } else {
      if (start) {
        this.reportStreamData[videoType].timestamp = this.reportStreamData.main.timestamp
        this.reportStreamData[videoType].diffServerTime = this.reportStreamData.main.diffServerTime
        this.reportStreamData[videoType].streamId = streamId
        return
      }
      if (!this.reportStreamData[videoType].timestamp) {
        const res: any = await Common.getGlobalTimestamp().catch(() => {
          this.reportStreamData.showAgainLoadModal = true
          return
        })
        if (!res || !res.body) {
          this.reportStreamData.showAgainLoadModal = true
          return
        }
        this.reportStreamData[videoType].timestamp = parseInt(res.body.timestamp, 10)
        this.reportStreamData[videoType].diffServerTime = this.reportStreamData[videoType].timestamp - Date.now()
        this.reportStreamData[videoType].streamId = streamId
      }
    }
  }
  private reportStreamDataReaction = reaction(
    () => this.liveDataSource.length,
    async (liveDataSourceLength) => {
      // 初始化不处理当手动播放时去开启流数据上报
      if (this.lastLiveDataSource.length === 0 && liveDataSourceLength > 0) {
        this.lastLiveDataSource = this.liveDataSource.slice()
        // console.log('reportStreamDataReaction-初始化-this.lastLiveDataSource', this.lastLiveDataSource, this.lastLiveDataSource.length)
        if (this.reportStreamData.needRestartReport) {
          this.reportStreamData.needRestartReport = false
          this.startReportStreamData()
        }
        return
      }
      // 新增流
      if (this.lastLiveDataSource.length > 0 && liveDataSourceLength > this.lastLiveDataSource.length) {
        if (this.reportStreamData.playing) {
          const addData = _.differenceBy(this.liveDataSource, this.lastLiveDataSource, 'sid')
          // console.log('reportStreamDataReaction-新增-addData', addData)
          // 主流、分享流处理
          addData.filter(item => item.videoType !== 'line').forEach(addItem => {
            this.handleStreamStamp(`${addItem.sid}`, addItem.videoType)
          })
          // 连麦流处理
          const existLineLength = this.lastLiveDataSource.filter(item => item.videoType === 'line').length
          if (existLineLength === 1) {
            const addDataLine2 = addData.filter(item => item.videoType === 'line')[0]
            this.handleStreamStamp(`${addDataLine2.sid}`, !this.reportStreamData.line1.timestamp ? 'line1' : 'line2')
          } else if (existLineLength === 0) {
            const addDataLength = addData.filter(item => item.videoType === 'line').length
            if (addDataLength > 0) {
              const addDataLine1 = addData.filter(item => item.videoType === 'line')[0]
              this.handleStreamStamp(`${addDataLine1.sid}`, 'line1')
              if (addDataLength === 2) {
                const addDataLine22 = addData.filter(item => item.videoType === 'line')[1]
                this.handleStreamStamp(`${addDataLine22.sid}`, 'line2')
              }
            }
          }
        }
        this.lastLiveDataSource = this.liveDataSource.slice()
        return
      }
      // 结束某个流 或者全部结束
      if (this.lastLiveDataSource.length > 0 && liveDataSourceLength < this.lastLiveDataSource.length) {
        if (this.reportStreamData.playing) {
          const deleteData = _.differenceBy(this.lastLiveDataSource, this.liveDataSource, 'sid')
          // console.log('reportStreamDataReaction-删除-deleteData', deleteData)
          if (deleteData.length > 0) {
            this.reportStreamData.needReportType = 1
          }
          deleteData.forEach(deleteItem => {
            ['main', 'share', 'line1', 'line2'].forEach(item => {
              if (this.reportStreamData[item].streamId === `${deleteItem.sid}`) {
                this.reportStreamData.resetDataTypes.push(`${item}`)
              }
            })
          })
        }
        this.lastLiveDataSource = this.liveDataSource.slice()
      }
    }
  )
  private resetDeleteStreamTypeReaction = reaction(
    () => this.reportStreamData.needReportType,
    (needReportType) => {
      if (needReportType === 3) {
        this.reportStreamData.resetDataTypes.forEach(item => {
          this.reportStreamData[item].timestamp = 0
          this.reportStreamData[item].diffServerTime = 0
          this.reportStreamData[item].streamId = ''
        })
        this.reportStreamData.resetDataTypes = []
        this.reportStreamData.needReportType = 0
        if (this.liveDataSource.length === 0) {
          this.reportStreamData.playing = false
          this.reportStreamData.needRestartReport = false
        }
      }
    }
  )
  @action
  public setGlobalSensitiveWord = async () => {
    const result: any = await LiveRoom.getSensitiveWord()
    const { words = [] } = result
    this.globalSensitiveWord = words
  }
  @action
  public setLiveRoomInfo = (room: ILiveRoomDetail, initial: boolean) => {
    this.liveRoomDetail = room
    if (initial) {
      this.liveDataSource = []
    }
  }
  @action
  public updateRoomInfo = (data: Partial<ILiveRoomDetail>) => {
    this.liveRoomDetail = { ...this.liveRoomDetail, ...data }
  }
  @action
  public deleteliveDataSoure = (stream: any) => {
    if (this.liveDataSource.length === 0) {
      return
    }
    const tmpArr = this.liveDataSource.slice()
    tmpArr.some((data, index) => {
      if (data.sid === stream.sid) {
        tmpArr.splice(index, 1)
        return true
      }
      return false
    })
    if (tmpArr.length === 1) {
      // 解决切换视频后，只剩下一个流时，设置为main
      delete tmpArr[0].switchType
    }
    this.liveDataSource = tmpArr
    if (this.liveDataSource.length === 0 && !this.isStepOut) {
      this.isStepOut = true
    }
  }
  @action
  public anchorLeave = () => {
    this.isStepOut = true
    this.liveDataSource = []
  }
  private getVideoConfig = (stream: any, videoType: string) => {
    const config = {
      hasAudio: true,
    } as {
      hasAudio?: boolean
      hasVideo?: boolean
    }
    if (videoType === 'share') {
      config.hasAudio = streamTypeEnum.PC_MEDIA === stream.st
    }
    if (videoType === 'line') {
      const micinfo =
        this.micData.filter(data => stream.uid === data.userId)[0] || {}
      config.hasVideo = micinfo.connectType === '1'
    }
    if (this.liveRoomDetail.liveMode === 1) {
      config.hasVideo = false
    } else if (videoType === 'share' || videoType === 'main') {
      config.hasVideo = true
    }
    return config
  }

  @action
  public addliveDataSoureByStream = (
    source: any[] = [],
    micData: IMicData[]
  ) => {
    this.addliveDataSoure(source)
    this.updateliveDataSoure(micData)
  }

  @action
  public updateliveDataSoure = (micData: IMicData[]) => {
    this.micData = micData
    const liveDataSource = this.liveDataSource.slice()
    liveDataSource
      .filter(item => item.videoType === 'line')
      .forEach((item, index) => {
        micData.some(data => {
          let flag = false
          if (item.uid === data.userId && item.hasVideo === undefined) {
            item.hasVideo = data.connectType === '1'
            flag = true
          }
          return flag
        })
      })
    this.liveDataSource = liveDataSource
  }


  @action
  public turnMicDealDataSource = () => {
    this.liveDataSource = []
    this.masterId = ''
  }

  @action setPlayUrlPreObj = value => {
    this.playUrlPreObj = value
  }

  public updateLiveDataSource(source: any[] = []) {
    this.liveDataSource = source
  }

  private formdataVideoSource = (
    stream: any,
    videoType: string,
    videoUserImg: string,
    videoUserName: string
  ) => {
    const { hasVideo, hasAudio } = this.getVideoConfig(stream, videoType)
    const isIe = isIE()
    return {
      sid: stream.sid,
      uid: stream.uid,
      url: isIe
        ? `https://hls-${stream.dn}/${stream.can}/${stream.sn}.m3u8`
        : `https://flv-${stream.dn}/${stream.can}/${stream.sn}.flv`,
      videoType,
      sourceType: isIe ? 'm3u8' : 'flv',
      videoUserImg,
      videoUserName,
      hasVideo,
      hasAudio,
    }
  }

  // private getVideoType (stream: any) {

  // }
}

const LiveStream = new LiveStreamState()
export type ILiveStreamState = typeof LiveStream

export default LiveStream
